// The start page of a server of several worlds (milestone 8, design §14.3,
// §14.4). One page for several addresses:
// - /                 logging in (or looking as a guest), then the worlds and the role in each;
// - /invite/<token>   choosing a password from an invite link;
// - /accounts         the server admins' accounts page;
// - /rights/<world>   who may see and edit a world, for its admins.

import { clearMaps } from './map-cache.ts';
import { button, dialog, el } from './ui.ts';

interface Me {
  account: { name: string; admin: boolean } | null;
  /** Without accounts everyone is the editor (trying it out on one's own computer). */
  local: boolean;
  /** Accounts are on, but none has been made yet. */
  firstRun: boolean;
}

interface HubWorld {
  id: string;
  name: string;
  role: 'viewer' | 'editor' | 'admin';
  clearance: string | null;
  clearanceName: string | null;
}

type Right = 'none' | 'viewer' | 'editor' | 'admin';
interface Grant {
  role: Right;
  tier?: string;
}

const root = document.getElementById('hub')!;

/** Calls the server; throws with its message when it refuses. */
async function call<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
  const res = await fetch(path, {
    method: init?.method ?? 'GET',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: init?.body === undefined ? undefined : JSON.stringify(init.body),
  });
  const body = await res.json().catch(() => ({})) as T & { error?: string };
  if (!res.ok) throw Object.assign(new Error(body.error ?? `The server answered ${res.status}`), { status: res.status });
  return body;
}

const field = (label: string, input: HTMLElement) => el('label', { class: 'hub-field' }, el('span', {}, label), input);
const input = (attrs: Record<string, string>) => el('input', attrs) as HTMLInputElement;
const note = (text: string) => el('p', { class: 'panel-note' }, text);
const errorLine = () => el('p', { class: 'hub-error', role: 'alert' });
const dateOf = (ms: number) => new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'long' });

/** Where to go after logging in: back to the world that sent you here, else the list. */
function next(): string | null {
  const n = new URLSearchParams(location.search).get('next');
  return n && n.startsWith('/worlds/') ? n : null;
}

/** A link shown to copy: selected on focus, and copied where the browser allows it. */
function linkBox(link: string): HTMLElement {
  const box = input({ type: 'text', value: link, readonly: 'true', class: 'hub-link', 'aria-label': 'Link' });
  box.addEventListener('focus', () => box.select());
  return el('div', { class: 'hub-actions' }, box, button('Copy', () => {
    box.select();
    // The clipboard is there only on HTTPS or this computer; copying the selected text works everywhere.
    const copied = navigator.clipboard?.writeText(link);
    if (copied) void copied.catch(() => document.execCommand('copy'));
    else document.execCommand('copy');
  }));
}

// --- Logging in ---------------------------------------------------------------------

function loginPage(me: Me) {
  const name = input({ type: 'text', autocomplete: 'username', autofocus: 'true' });
  const password = input({ type: 'password', autocomplete: 'current-password' });
  const remember = input({ type: 'checkbox' });
  const err = errorLine();
  const form = el('form', { class: 'hub-card' },
    el('h1', {}, 'Atlas'),
    field('Username', name),
    field('Password', password),
    el('label', { class: 'check-row' }, remember, 'Keep me signed in on this browser'),
    err,
    el('button', { type: 'submit', class: 'primary' }, 'Log in'),
    el('p', { class: 'panel-note hub-sep' }, 'No account, or only looking?'),
    button('View as guest', () => {
      try {
        localStorage.setItem('atlas-guest', 'yes');
      } catch {
        /* storage may be unavailable */
      }
      void listPage(me);
    }),
    note(me.firstRun ? 'No accounts yet. Make yours in a terminal on the server: npm run accounts -- add <your name> --admin' : 'New here? Ask the server\'s admin for an invite link.'));
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    err.textContent = '';
    try {
      await call('/api/login', { method: 'POST', body: { name: name.value, password: password.value, remember: remember.checked } });
      const n = next();
      if (n) location.assign(n);
      else void home();
    } catch (x) {
      err.textContent = (x as Error).message;
      password.select();
    }
  });
  root.replaceChildren(form);
  name.focus();
}

// --- The worlds -----------------------------------------------------------------------

function roleText(w: HubWorld): string {
  if (w.role === 'admin') return 'Admin';
  if (w.role === 'editor') return 'Editor';
  return w.clearanceName ? `Viewer, sees ${w.clearanceName}` : 'Viewer';
}

async function listPage(me: Me) {
  const head = el('div', { class: 'hub-head' }, el('h1', {}, 'Worlds'), el('span', { class: 'spacer' }));
  const extra = el('div');
  if (me.account) {
    head.append(el('span', { class: 'hub-who' }, `Signed in as ${me.account.name}`));
    if (me.account.admin) head.append(el('a', { class: 'button-link', href: '/accounts' }, 'Accounts'));
    head.append(button('Change password', () => passwordForm(extra)), button('Sign out', async () => {
      // Maps kept in this browser go too: the next person here should not find them.
      await clearMaps();
      await call('/api/logout', { method: 'POST' }).catch(() => null);
      void home();
    }));
  } else if (!me.local) {
    head.append(el('span', { class: 'hub-who' }, 'Looking as a guest'), button('Log in', () => {
      try {
        localStorage.removeItem('atlas-guest');
      } catch {
        /* storage may be unavailable */
      }
      loginPage(me);
    }));
  }
  const list = el('div');
  root.replaceChildren(head, extra, list);
  let worlds: HubWorld[];
  try {
    worlds = (await call<{ worlds: HubWorld[] }>('/api/worlds')).worlds;
  } catch (x) {
    list.replaceChildren(note(`The list of worlds could not be loaded: ${(x as Error).message}`));
    return;
  }
  if (!worlds.length) {
    list.replaceChildren(note(me.local
      ? 'There are no worlds on this server yet. Put world folders, each with its world.json, in the worlds folder.'
      : me.account ? 'You have no worlds here yet. Ask the server\'s admin for access.' : 'No worlds are open to guests. Log in to see yours.'));
    return;
  }
  list.replaceChildren(...worlds.map((w) => el('div', { class: 'hub-row' },
    el('span', { class: 'hub-name' }, el('strong', {}, w.name), w.name !== w.id ? el('span', { class: 'hub-id' }, ` ${w.id}`) : null),
    el('span', { class: 'hub-role' }, roleText(w)),
    w.role === 'admin' && !me.local ? el('a', { class: 'button-link', href: `/rights/${encodeURIComponent(w.id)}` }, 'Rights') : null,
    el('a', { class: 'button-link primary', href: `/worlds/${encodeURIComponent(w.id)}/` }, 'Open'))));
}

function passwordForm(into: HTMLElement) {
  if (into.firstChild) {
    into.replaceChildren();
    return;
  }
  const current = input({ type: 'password', autocomplete: 'current-password' });
  const fresh = input({ type: 'password', autocomplete: 'new-password' });
  const again = input({ type: 'password', autocomplete: 'new-password' });
  const err = errorLine();
  const form = el('form', { class: 'hub-card hub-inline' },
    el('h2', {}, 'Change your password'),
    field('Current password', current), field('New password', fresh), field('The same again', again), err,
    el('button', { type: 'submit', class: 'primary' }, 'Change password'));
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (fresh.value !== again.value) {
      err.textContent = 'The two new passwords are not the same';
      return;
    }
    try {
      await call('/api/me/password', { method: 'POST', body: { current: current.value, password: fresh.value } });
      into.replaceChildren(note('Your password is changed.'));
    } catch (x) {
      err.textContent = (x as Error).message;
    }
  });
  into.replaceChildren(form);
  current.focus();
}

// --- Invite links -------------------------------------------------------------------

async function invitePage(token: string) {
  let info: { name: string; reset: boolean; expires: number };
  try {
    info = await call(`/api/invites/${encodeURIComponent(token)}`);
  } catch (x) {
    root.replaceChildren(el('div', { class: 'hub-card' }, el('h1', {}, 'Atlas'), note((x as Error).message), el('a', { class: 'button-link', href: '/' }, 'To the start page')));
    return;
  }
  const fresh = input({ type: 'password', autocomplete: 'new-password' });
  const again = input({ type: 'password', autocomplete: 'new-password' });
  const err = errorLine();
  const form = el('form', { class: 'hub-card' },
    el('h1', {}, info.reset ? `A new password for ${info.name}` : `Welcome, ${info.name}`),
    note(`Choose ${info.reset ? 'a new' : 'a'} password for your Atlas account. This link works once, until ${dateOf(info.expires)}.`),
    field('Password', fresh), field('The same again', again), err,
    el('button', { type: 'submit', class: 'primary' }, 'Set password and continue'));
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (fresh.value !== again.value) {
      err.textContent = 'The two passwords are not the same';
      return;
    }
    try {
      await call(`/api/invites/${encodeURIComponent(token)}`, { method: 'POST', body: { password: fresh.value } });
      location.assign('/');
    } catch (x) {
      err.textContent = (x as Error).message;
    }
  });
  root.replaceChildren(form);
  fresh.focus();
}

// --- Accounts -------------------------------------------------------------------------

interface AccountRow {
  id: string;
  name: string;
  admin: boolean;
  active: boolean;
  link?: string;
  expires?: number;
  reset?: boolean;
}

/** The accounts page; `show`, a link just made, shown under the list. */
async function accountsPage(me: Me, show?: { title: string; link: string }) {
  const head = el('div', { class: 'hub-head' }, el('h1', {}, 'Accounts'), el('span', { class: 'spacer' }), el('a', { class: 'button-link', href: '/' }, 'All worlds'));
  const list = el('div'), shown = el('div');
  root.replaceChildren(head, list, shown);
  let rows: AccountRow[];
  try {
    rows = (await call<{ accounts: AccountRow[] }>('/api/accounts')).accounts;
  } catch (x) {
    list.replaceChildren(note((x as Error).message));
    return;
  }
  const showLink = (title: string, link: string) => shown.replaceChildren(el('div', { class: 'hub-card hub-inline' }, el('h2', {}, title), note('Send this link; it works once.'), linkBox(link)));
  list.replaceChildren(...rows.map((a) => {
    const state = a.admin ? 'server admin' : !a.active ? (a.link ? `invited, link valid until ${dateOf(a.expires!)}` : 'invited, link expired') : a.link ? `new password link valid until ${dateOf(a.expires!)}` : '';
    return el('div', { class: 'hub-row' },
      el('span', { class: 'hub-name' }, el('strong', {}, a.name), state ? el('span', { class: 'hub-id' }, ` ${state}`) : null),
      a.link ? button(a.active ? 'Copy password link' : 'Copy invite link', () => showLink(`Link for ${a.name}`, a.link!)) : null,
      button(a.active ? 'New password link' : 'New invite link', async () => {
        try {
          const r = await call<{ link: string }>(`/api/accounts/${encodeURIComponent(a.id)}/link`, { method: 'POST' });
          await accountsPage(me, { title: `Link for ${a.name}`, link: r.link });
        } catch (x) {
          shown.replaceChildren(note((x as Error).message));
        }
      }),
      me.account?.name === a.name ? null : button('Remove', async () => {
        if (!(await dialog(`Remove ${a.name}?`, 'They can no longer log in, and lose their rights in every world.', [['remove', 'Remove']]))) return;
        try {
          await call(`/api/accounts/${encodeURIComponent(a.id)}`, { method: 'DELETE' });
          await accountsPage(me);
        } catch (x) {
          shown.replaceChildren(note((x as Error).message));
        }
      }));
  }));
  const name = input({ type: 'text', placeholder: 'Username', 'aria-label': 'Username of the new account' });
  const err = errorLine();
  const add = el('form', { class: 'hub-actions hub-add' }, el('span', { class: 'hub-who' }, 'New account'), name,
    el('button', { type: 'submit', class: 'primary' }, 'Create invite link'), err);
  add.addEventListener('submit', async (e) => {
    e.preventDefault();
    try {
      const r = await call<{ link: string }>('/api/accounts', { method: 'POST', body: { name: name.value } });
      await accountsPage(me, { title: `Invite link for ${name.value.trim()}`, link: r.link });
    } catch (x) {
      err.textContent = (x as Error).message;
    }
  });
  list.append(add);
  if (show) showLink(show.title, show.link);
}

// --- Rights ---------------------------------------------------------------------------

async function rightsPage(world: string) {
  const head = el('div', { class: 'hub-head' }, el('h1', {}, 'Rights'), el('span', { class: 'spacer' }), el('a', { class: 'button-link', href: '/' }, 'All worlds'));
  const body = el('div');
  root.replaceChildren(head, body);
  let info: { world: { id: string; name: string; tiers: { id: string; name: string }[] }; guest: Grant; people: { id: string; name: string; serverAdmin: boolean; grant: Grant }[] };
  try {
    info = await call(`/api/worlds/${encodeURIComponent(world)}/rights`);
  } catch (x) {
    body.replaceChildren(note((x as Error).message));
    return;
  }
  (head.firstChild as HTMLElement).textContent = `Rights for ${info.world.name}`;
  const tiers = info.world.tiers;
  const labels: Record<Right, string> = { none: 'None', viewer: 'Viewer', editor: 'Editor', admin: 'Admin' };
  /** A row's choices: its rights, and for viewers the tier they see up to. */
  const choose = (g: Grant, options: Right[], label: string) => {
    const role = el('select', { 'aria-label': `Rights of ${label}` }, ...options.map((r) => new Option(labels[r], r))) as HTMLSelectElement;
    role.value = g.role;
    const tier = el('select', { 'aria-label': `What ${label} sees` }, ...tiers.map((t) => new Option(t.name, t.id))) as HTMLSelectElement;
    tier.value = g.tier && tiers.some((t) => t.id === g.tier) ? g.tier : tiers[0]?.id ?? '';
    const sync = () => (tier.hidden = role.value !== 'viewer');
    role.addEventListener('change', sync);
    sync();
    return { role, tier, read: (): Grant => (role.value === 'viewer' ? { role: 'viewer', tier: tier.value } : { role: role.value as Right }) };
  };
  const guest = choose(info.guest, ['none', 'viewer'], 'guests');
  const people = info.people.map((p) => ({ p, c: p.serverAdmin ? null : choose(p.grant, ['none', 'viewer', 'editor', 'admin'], p.name) }));
  const status = el('p', { class: 'panel-note', role: 'status' });
  const table = el('table', { class: 'hub-table' },
    el('tr', {}, el('th', {}, 'Who'), el('th', {}, 'Rights'), el('th', {}, 'Sees (viewers)')),
    el('tr', {}, el('td', {}, 'Guests (not signed in)'), el('td', {}, guest.role), el('td', {}, guest.tier)),
    ...people.map(({ p, c }) => el('tr', {},
      el('td', {}, p.name),
      el('td', {}, c ? c.role : 'Admin (server admin)'),
      el('td', {}, c ? c.tier : el('span', { class: 'hub-id' }, 'everything')))));
  const save = button('Save', async () => {
    try {
      await call(`/api/worlds/${encodeURIComponent(world)}/rights`, {
        method: 'PUT',
        body: { guest: guest.read(), people: Object.fromEntries(people.filter((x) => x.c).map(({ p, c }) => [p.id, c!.read()])) },
      });
      status.textContent = 'Saved. It holds from the next thing each person opens or reloads.';
    } catch (x) {
      status.textContent = (x as Error).message;
    }
  }, { class: 'primary' });
  body.replaceChildren(table, note('Viewers see the world up to their tier; editors and admins see everything, and admins can change this page. Someone with an account never has less than a guest.'), el('div', { class: 'hub-actions' }, save), status);
}

// --- Which page -------------------------------------------------------------------------

async function home() {
  let me: Me;
  try {
    me = await call<Me>('/api/me');
  } catch (x) {
    root.replaceChildren(note(`The server could not be reached: ${(x as Error).message}`));
    return;
  }
  history.replaceState(null, '', '/' + location.search);
  if (me.local || me.account) return listPage(me);
  let guest = false;
  try {
    guest = localStorage.getItem('atlas-guest') === 'yes';
  } catch {
    /* storage may be unavailable */
  }
  // Sent here by a world that needs a login: log in, even if you were looking as a guest.
  if (guest && !next()) return listPage(me);
  loginPage(me);
}

async function route() {
  const path = location.pathname;
  let m: RegExpExecArray | null;
  if ((m = /^\/invite\/([^/]+)$/.exec(path))) return invitePage(decodeURIComponent(m[1]!));
  if ((m = /^\/rights\/([^/]+)$/.exec(path))) return rightsPage(decodeURIComponent(m[1]!));
  if (path === '/accounts') {
    const me = await call<Me>('/api/me').catch(() => null);
    if (!me?.account) return home();
    return accountsPage(me);
  }
  return home();
}

void route();
